import React from 'react';
import PropTypes from 'prop-types';
import { ReactSVG } from 'react-svg';
import { useTranslation } from 'react-i18next';
import { Margin } from 'styled-components-spacing';
import { iconList } from 'assets';
import { Field, FormSection, Radio, Reveal, Select } from 'components';
import QUESTIONS from './questions';
import { RadioRow, NarrowInput, EmptyNote, ProxyRow, ProxyBadge } from './Application.styled';

/**
 * The questions of one meeting type.
 *
 * The Member Association fills this in and both profiles read it back: the same
 * component renders the editable form and the read only view, which is why the
 * proxy is either a select (while applying) or the Member Association that was
 * picked together with their answer (once the application was sent).
 */
const Questionnaire = ({
  meeting,
  answers,
  errors,
  disabled,
  onAnswer,
  proxy,
  proxyOptions,
  onProxy,
  proxyApproved,
  proxyDisplay,
  votingRepresentative,
  observer,
}) => {
  const { t } = useTranslation();

  const answerOf = (question) => answers[question];

  const renderChoices = (question, options, label, required = true) => (
    <Margin bottom="20">
      <Field id={question} label={label} required={required} error={errors[question]}>
        <RadioRow>
          {options.map((option) => (
            <Radio
              key={`${question}-${String(option.value)}`}
              disabled={disabled}
              selected={answerOf(question) === option.value}
              onClick={() => onAnswer(question, option.value)}
            >
              {option.label}
            </Radio>
          ))}
        </RadioRow>
      </Field>
    </Margin>
  );

  const yesNo = [
    { value: true, label: t('global.yes') },
    { value: false, label: t('global.no') },
  ];

  const proxyBadgeLabel = (approved) => {
    if (approved === true) return t('page.ma.meetings.application.proxyApproved');
    if (approved === false) return t('page.ma.meetings.application.proxyRejected');
    return t('page.ma.meetings.application.proxyPending');
  };

  const proxyBadgeIcon = (approved) => {
    if (approved === true) return iconList.checkCircle;
    if (approved === false) return iconList.closeCircle;
    return iconList.clock;
  };

  // A sent application names the Member Association that carries the vote and
  // whether they confirmed the claim, instead of offering the choice again.
  const renderProxyStatus = () => (
    <Reveal>
      <Margin bottom="20">
        <Field
          id="proxyMemberAssociation"
          label={t('page.ma.meetings.application.proxyMemberAssociation')}
        >
          <ProxyRow>
            <span>{proxyDisplay.label}</span>
            <ProxyBadge approved={proxyDisplay.approved}>
              <ReactSVG src={proxyBadgeIcon(proxyDisplay.approved) || ''} />
              {proxyBadgeLabel(proxyDisplay.approved)}
            </ProxyBadge>
          </ProxyRow>
        </Field>
      </Margin>
    </Reveal>
  );

  // Either the list of Member Associations that can carry the vote, or the note
  // explaining why there is nothing to pick yet - never both.
  const renderProxySelect = () => (
    <Reveal>
      <Margin bottom="20">
        <Field
          id="proxyMemberAssociation"
          label={t('page.ma.meetings.application.proxyMemberAssociation')}
          description={
            proxyOptions.length ? t('page.ma.meetings.application.proxyHelp') : undefined
          }
          required={!!proxyOptions.length}
          error={errors.proxyMemberAssociation}
        >
          {proxyOptions.length ? (
            <NarrowInput>
              <Select
                id="proxyMemberAssociation"
                type="text"
                disabled={disabled}
                placeholder={t('page.ma.meetings.application.proxyPlaceholder')}
                value={proxyOptions.find((option) => option.value === proxy) || ''}
                options={proxyOptions}
                onChange={(option) => onProxy(option ? option.value : null)}
              />
            </NarrowInput>
          ) : (
            <EmptyNote>{t('page.ma.meetings.application.proxyEmpty')}</EmptyNote>
          )}
        </Field>
        {proxyApproved === true && (
          <EmptyNote>{t('page.ma.meetings.application.proxyApproved')}</EmptyNote>
        )}
        {proxyApproved === false && (
          <EmptyNote>{t('page.ma.meetings.application.proxyRejected')}</EmptyNote>
        )}
        {proxyApproved === null && proxy && (
          <EmptyNote>{t('page.ma.meetings.application.proxyPending')}</EmptyNote>
        )}
      </Margin>
    </Reveal>
  );

  const renderProxy = () => (proxyDisplay ? renderProxyStatus() : renderProxySelect());

  // The dinner is a group of its own, so it is rendered next to the attendance
  // questions rather than inside them - but it is still only asked once the
  // answers there say somebody is actually coming.
  const renderDinner = () => (
    <>
      {renderChoices(
        QUESTIONS.DINNER_PARTICIPATION,
        yesNo,
        t('page.ma.meetings.application.dinner'),
      )}
      {answerOf(QUESTIONS.DINNER_PARTICIPATION) === true && (
        <Reveal>
          <Margin bottom="20">
            <Field
              id={QUESTIONS.DINNER_PLUS_ONE}
              label={t('page.ma.meetings.application.dinnerPlusOne')}
              description={t('page.ma.meetings.application.dinnerPlusOneHelp')}
              error={errors[QUESTIONS.DINNER_PLUS_ONE]}
            >
              <NarrowInput>
                <input
                  id={QUESTIONS.DINNER_PLUS_ONE}
                  type="text"
                  disabled={disabled}
                  value={answerOf(QUESTIONS.DINNER_PLUS_ONE) || ''}
                  placeholder={t('page.ma.meetings.application.dinnerPlusOnePlaceholder')}
                  onChange={(event) => onAnswer(QUESTIONS.DINNER_PLUS_ONE, event.target.value)}
                />
              </NarrowInput>
            </Field>
          </Margin>
        </Reveal>
      )}
    </>
  );

  // A substitute hands the vote to the delegate and the other way around; the
  // neutral wording is used when the reader holds neither of the two roles.
  let representativeLabel = 'page.ma.meetings.application.votingRepresentative';
  if (votingRepresentative === 'DELEGATE') {
    representativeLabel = 'page.ma.meetings.application.votingDelegate';
  } else if (votingRepresentative === 'SUBSTITUTE') {
    representativeLabel = 'page.ma.meetings.application.votingSubstitute';
  }

  const renderCod = () => {
    const participates = answerOf(QUESTIONS.COD_PARTICIPATION);

    // An observer registers for the meeting and nothing else: the vote, the
    // proxy that carries it and the dinner belong to a seat they do not hold.
    if (observer) {
      return (
        <>
          {renderChoices(
            QUESTIONS.COD_FRT_PARTICIPATION,
            yesNo,
            t('page.ma.meetings.application.frtParticipation'),
          )}
          {renderChoices(
            QUESTIONS.COD_PARTICIPATION,
            yesNo,
            t('page.ma.meetings.application.codParticipation'),
          )}
        </>
      );
    }

    return (
      <>
        {renderChoices(
          QUESTIONS.COD_FRT_PARTICIPATION,
          yesNo,
          t('page.ma.meetings.application.frtParticipation'),
        )}
        {renderChoices(
          QUESTIONS.COD_PARTICIPATION,
          yesNo,
          t('page.ma.meetings.application.codParticipation'),
        )}

        {participates === false && (
          <Reveal>
            {renderChoices(
              QUESTIONS.COD_ABSENCE,
              [
                {
                  value: QUESTIONS.ABSENCE_SUBSTITUTE,
                  label: t('page.ma.meetings.application.absenceSubstitute'),
                },
                {
                  value: QUESTIONS.ABSENCE_PROXY,
                  label: t('page.ma.meetings.application.absenceProxy'),
                },
              ],
              t('page.ma.meetings.application.absence'),
            )}
            {answerOf(QUESTIONS.COD_ABSENCE) === QUESTIONS.ABSENCE_PROXY && renderProxy()}
          </Reveal>
        )}

        {participates === true && (
          <Reveal>
            {renderChoices(
              QUESTIONS.COD_VOTING,
              [
                {
                  value: QUESTIONS.VOTING_SELF,
                  label: t('page.ma.meetings.application.votingSelf'),
                },
                {
                  value: QUESTIONS.VOTING_REPRESENTATIVE,
                  label: t(representativeLabel),
                },
                {
                  value: QUESTIONS.VOTING_PROXY,
                  label: t('page.ma.meetings.application.votingProxy'),
                },
              ],
              t('page.ma.meetings.application.voting'),
            )}
            {answerOf(QUESTIONS.COD_VOTING) === QUESTIONS.VOTING_PROXY && renderProxy()}
          </Reveal>
        )}
      </>
    );
  };

  const renderCsp = () => (
    <>
      {renderChoices(
        QUESTIONS.CSP_PARTICIPATION,
        yesNo,
        t('page.ma.meetings.application.cspParticipation'),
      )}
    </>
  );

  const isCod = meeting.type === 'COD';

  // An online meeting has no dinner, and a COD delegate who is not coming is
  // not asked about it either. This is exactly when the question used to be
  // rendered inside the attendance answers, and the API still validates it on
  // the same terms - moving the question into a group of its own must not
  // quietly make it required in a case where it was never asked before.
  //
  // An observer registers for the meeting and nothing else, so the dinner is
  // not theirs to answer - the group they see holds the attendance alone. The
  // API leaves the question out of their form for the same reason.
  const showDinner =
    !observer && meeting.onsite && (!isCod || answerOf(QUESTIONS.COD_PARTICIPATION) === true);

  const dinnerSection = (
    <FormSection
      title={t('page.ma.meetings.application.dinnerSection')}
      description={t('page.ma.meetings.application.dinnerSectionHelp')}
    >
      {renderDinner()}
    </FormSection>
  );

  return (
    <>
      <FormSection
        title={t('page.ma.meetings.application.attendanceSection')}
        description={t('page.ma.meetings.application.attendanceSectionHelp')}
      >
        {isCod ? renderCod() : renderCsp()}
      </FormSection>

      {/* On a CSP the group is there from the start, so it is not eased in -
          only the COD one appears in answer to something the user did. */}
      {showDinner && isCod ? <Reveal>{dinnerSection}</Reveal> : null}
      {showDinner && !isCod ? dinnerSection : null}
    </>
  );
};

Questionnaire.propTypes = {
  meeting: PropTypes.shape({
    type: PropTypes.string,
    onsite: PropTypes.bool,
  }).isRequired,
  answers: PropTypes.shape({}),
  errors: PropTypes.objectOf(PropTypes.string),
  disabled: PropTypes.bool,
  onAnswer: PropTypes.func,
  proxy: PropTypes.number,
  proxyOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.number, label: PropTypes.string }),
  ),
  onProxy: PropTypes.func,
  proxyApproved: PropTypes.bool,
  proxyDisplay: PropTypes.shape({
    label: PropTypes.string,
    approved: PropTypes.bool,
  }),
  votingRepresentative: PropTypes.string,
  observer: PropTypes.bool,
};

Questionnaire.defaultProps = {
  answers: {},
  errors: {},
  disabled: false,
  onAnswer: () => {},
  proxy: null,
  proxyOptions: [],
  onProxy: () => {},
  proxyApproved: null,
  proxyDisplay: null,
  votingRepresentative: null,
  observer: false,
};

export default Questionnaire;
